/**
 * Goofy loading phrases for the progress indicator during dialogue generation.
 * Each language has 15-25 fun phrases about language learning, grammar, and AI processing.
 */

import type { LocaleCode } from "@/lib/api/client";

const loadingPhrases: Record<string, string[]> = {
  en: [
    "Befriending grammar...",
    "Herding vowels...",
    "Conjugating furiously...",
    "Whispering to the AI...",
    "Brewing vocabulary...",
    "Untangling syntax...",
    "Polishing pronouns...",
    "Teaching verbs to dance...",
    "Negotiating with nouns...",
    "Convincing adjectives to cooperate...",
    "Warming up the language engine...",
    "Feeding the neural networks...",
    "Alphabetizing thoughts...",
    "Summoning the grammar gods...",
    "Wrangling irregular verbs...",
    "Sprinkling linguistic fairy dust...",
    "Consulting the dictionary elves...",
    "Massaging the morphemes...",
    "Tickling the tenses...",
    "Assembling syllables...",
    // From plan - additional phrases
    "Wrangling participles...",
    "Tickling the thesaurus...",
    "Consulting ancient scrolls...",
    "Charming the dictionary...",
    "Rehearsing pronunciations...",
    "Taming wild tenses...",
    "Summoning synonyms...",
    "Petting prepositions...",
    "Chasing clauses...",
    "Dusting off dialects...",
    "Stretching sentences...",
    "Composing cleverness...",
    "Harvesting phrases...",
    "Percolating prose...",
  ],
  es: [
    "Domesticando verbos...",
    "Conjugando con pasión...",
    "Susurrando al algoritmo...",
    "Acariciando las tildes...",
    "Negociando con la gramática...",
    "Alimentando neuronas artificiales...",
    "Ordenando las palabras...",
    "Puliendo los pronombres...",
    "Despertando el subjuntivo...",
    "Convenciendo a los acentos...",
    "Bailando con los verbos...",
    "Horneando vocabulario...",
    "Entrenando consonantes...",
    "Afinando la sintaxis...",
    "Reuniendo las letras...",
    "Invocando el espíritu del español...",
    "Desenredando oraciones...",
    "Mimando los modismos...",
    // From plan - additional phrases
    "Domando tiempos verbales...",
    "Persiguiendo cláusulas...",
    "Consultando pergaminos...",
    "Encantando la gramática...",
    "Cosechando frases...",
    "Estirando oraciones...",
    "Invocando sinónimos...",
  ],
  pt: [
    "Domando verbos rebeldes...",
    "Conjugando com carinho...",
    "Sussurrando para a IA...",
    "Acariciando os acentos...",
    "Negociando com a gramática...",
    "Alimentando neurônios digitais...",
    "Organizando palavras...",
    "Polindo pronomes...",
    "Acordando o subjuntivo...",
    "Convencendo as cedilhas...",
    "Dançando com os verbos...",
    "Preparando vocabulário...",
    "Treinando consoantes...",
    "Afinando a sintaxe...",
    "Reunindo as sílabas...",
    "Invocando o espírito português...",
    "Desembaraçando frases...",
    "Mimando os ditongos...",
    // From plan - additional phrases
    "Domando tempos verbais...",
    "Perseguindo cláusulas...",
    "Consultando pergaminhos...",
    "Encantando a gramática...",
    "Colhendo frases...",
    "Esticando frases...",
    "Invocando sinónimos...",
  ],
  fr: [
    "Apprivoisant la grammaire...",
    "Conjuguant avec élégance...",
    "Murmurant à l'IA...",
    "Caressant les accents...",
    "Négociant avec les verbes...",
    "Nourrissant les neurones...",
    "Ordonnant les mots...",
    "Polissant les pronoms...",
    "Réveillant le subjonctif...",
    "Persuadant les cédilles...",
    "Dansant avec les liaisons...",
    "Préparant le vocabulaire...",
    "Entraînant les consonnes...",
    "Accordant la syntaxe...",
    "Rassemblant les syllabes...",
    "Invoquant l'esprit français...",
    "Démêlant les phrases...",
    "Chouchoutant les accents circonflexes...",
    "Séduisant les adjectifs...",
    // From plan - additional phrases
    "Domptant les temps...",
    "Poursuivant les clauses...",
    "Consultant les parchemins...",
    "Charmant la grammaire...",
    "Récoltant des phrases...",
    "Étirant les phrases...",
    "Invoquant des synonymes...",
  ],
  de: [
    "Verben zähmen...",
    "Grammatik streicheln...",
    "Mit der KI flüstern...",
    "Umlaute polieren...",
    "Mit Artikeln verhandeln...",
    "Neuronale Netze füttern...",
    "Wörter sortieren...",
    "Pronomen putzen...",
    "Den Konjunktiv wecken...",
    "Komposita zusammenbauen...",
    "Mit Verben tanzen...",
    "Vokabeln brauen...",
    "Konsonanten trainieren...",
    "Syntax stimmen...",
    "Silben sammeln...",
    "Den deutschen Geist beschwören...",
    "Sätze entwirren...",
    "Deklinationen massieren...",
    "Lange Wörter bändigen...",
    // From plan - additional phrases
    "Dem Wörterbuch zuflüstern...",
    "Sätze ordnen...",
    "Akzente streicheln...",
    "Mit Substantiven verhandeln...",
    "Wütend konjugieren...",
    "Silben polieren...",
    "Zeitformen bändigen...",
    "Klauseln jagen...",
    "Pergamente konsultieren...",
    "Die Grammatik bezaubern...",
    "Phrasen ernten...",
    "Sätze dehnen...",
    "Synonyme beschwören...",
  ],
  it: [
    "Addomesticando i verbi...",
    "Coniugando con amore...",
    "Sussurrando all'IA...",
    "Accarezzando gli accenti...",
    "Negoziando con la grammatica...",
    "Nutrendo i neuroni digitali...",
    "Ordinando le parole...",
    "Lucidando i pronomi...",
    "Svegliando il congiuntivo...",
    "Convincendo le consonanti...",
    "Ballando con i verbi...",
    "Preparando il vocabolario...",
    "Allenando le vocali...",
    "Accordando la sintassi...",
    "Raccogliendo le sillabe...",
    "Invocando lo spirito italiano...",
    "Sbrogliando le frasi...",
    "Coccolare i modi verbali...",
    "Gesticolando grammaticalmente...",
    // From plan - additional phrases
    "Sussurrando al dizionario...",
    "Domando tempi verbali...",
    "Inseguendo clausole...",
    "Consultando pergamene...",
    "Incantando la grammatica...",
    "Raccogliendo frasi...",
    "Stirando frasi...",
    "Evocando sinonimi...",
  ],
  ru: [
    "Укрощаем глаголы...",
    "Спрягаем с любовью...",
    "Шепчем нейросети...",
    "Полируем падежи...",
    "Договариваемся с грамматикой...",
    "Кормим алгоритмы...",
    "Сортируем слова...",
    "Чистим местоимения...",
    "Будим совершенный вид...",
    "Убеждаем согласные...",
    "Танцуем с глаголами...",
    "Варим словарный запас...",
    "Тренируем ударения...",
    "Настраиваем синтаксис...",
    "Собираем слоги...",
    "Призываем дух русского языка...",
    "Распутываем предложения...",
    "Ласкаем причастия...",
    "Жонглируем падежами...",
    // From plan - additional phrases
    "Приручаю глаголы...",
    "Шепчу словарю...",
    "Упорядочиваю предложения...",
    "Глажу ударения...",
    "Торгуюсь с существительными...",
    "Яростно спрягаю...",
    "Укрощаю времена...",
    "Преследую придаточные...",
    "Сверяюсь со свитками...",
    "Очаровываю грамматику...",
    "Растягиваю предложения...",
    "Призываю синонимы...",
  ],
  ja: [
    "文法をなだめています...",
    "動詞と友達になっています...",
    "AIにささやいています...",
    "漢字を磨いています...",
    "助詞と交渉中...",
    "ニューラルネットワークに餌やり...",
    "言葉を並べています...",
    "敬語を練習中...",
    "ひらがなを整列させています...",
    "カタカナを説得中...",
    "動詞と踊っています...",
    "語彙を醸造中...",
    "発音を訓練中...",
    "文法を調整中...",
    "音節を集めています...",
    "日本語の精霊を召喚中...",
    "文章を解きほぐしています...",
    "活用形をマッサージ中...",
    // From plan - additional phrases
    "動詞を手なずけ中...",
    "辞書にささやき中...",
    "文を整理中...",
    "アクセントを撫で中...",
    "名詞と交渉中...",
    "猛烈に活用中...",
    "音節を磨き中...",
    "時制を調教中...",
    "節を追いかけ中...",
    "アルゴリズムに餌やり中...",
    "古文書を参照中...",
    "文法を魅了中...",
    "フレーズを収穫中...",
    "文を伸ばし中...",
    "類義語を召喚中...",
  ],
  ko: [
    "문법을 길들이는 중...",
    "동사와 친해지는 중...",
    "AI에게 속삭이는 중...",
    "한글을 다듬는 중...",
    "조사와 협상 중...",
    "신경망에 밥 주는 중...",
    "단어를 정렬하는 중...",
    "존댓말 연습 중...",
    "자음을 설득하는 중...",
    "모음과 춤추는 중...",
    "어휘를 양조하는 중...",
    "발음을 훈련하는 중...",
    "문법을 조율하는 중...",
    "음절을 모으는 중...",
    "한국어 정령을 소환 중...",
    "문장을 풀어내는 중...",
    "받침을 마사지하는 중...",
    "경어를 연마하는 중...",
    // From plan - additional phrases
    "동사 길들이는 중...",
    "사전에 속삭이는 중...",
    "문장 정리하는 중...",
    "악센트 어루만지는 중...",
    "명사와 협상 중...",
    "맹렬히 활용하는 중...",
    "음절 다듬는 중...",
    "시제 훈련시키는 중...",
    "절 쫓는 중...",
    "알고리즘 먹이는 중...",
    "고문서 참고하는 중...",
    "문법 매료시키는 중...",
    "구문 수확하는 중...",
    "문장 늘리는 중...",
    "동의어 소환하는 중...",
  ],
  "zh-CN": [
    "驯服语法中...",
    "和动词交朋友...",
    "向AI低语中...",
    "打磨汉字中...",
    "与声调谈判中...",
    "喂养神经网络...",
    "整理词汇中...",
    "擦亮量词中...",
    "唤醒成语中...",
    "说服偏旁部首...",
    "与动词共舞...",
    "酿造词汇中...",
    "训练发音中...",
    "调整语法中...",
    "收集音节中...",
    "召唤中文之灵...",
    "解开句子中...",
    "按摩笔画中...",
    "整理拼音中...",
    // From plan - additional phrases
    "驯服动词中...",
    "对字典低语中...",
    "整理句子中...",
    "抚摸声调中...",
    "与名词谈判中...",
    "疯狂变位中...",
    "打磨音节中...",
    "驯服时态中...",
    "追逐从句中...",
    "喂养算法中...",
    "查阅古籍中...",
    "迷住语法中...",
    "收获短语中...",
    "拉伸句子中...",
    "召唤同义词中...",
  ],
};

/**
 * Get a random loading phrase for the given locale.
 * Falls back to English if the language is not found.
 *
 * @param locale - The locale code (e.g., "en", "pt-PT", "zh-CN")
 * @returns A random loading phrase
 */
export function getRandomPhrase(locale: LocaleCode): string {
  // Extract base language from locale (e.g., "pt-PT" -> "pt")
  // Special case for zh-CN which should stay as-is
  const baseLanguage = locale === "zh-CN" ? "zh-CN" : locale.split("-")[0]!;

  // Get phrases for the language, falling back to English
  const phrases = loadingPhrases[baseLanguage] || loadingPhrases["en"]!;

  // Return a random phrase
  return phrases[Math.floor(Math.random() * phrases.length)]!;
}
